/**
 * Security review MED-004: helpers to guard against open-redirect / dangerous
 * URL schemes (`javascript:`, `file:`, `data:`, ...) before navigating to a
 * URL that originates from an API response, CMS content or a chat message.
 */

const SAFE_URL_SCHEMES = ['http:', 'https:', 'mailto:', 'tel:'];

/**
 * Returns true only when `url` resolves to an allow-listed scheme
 * (http/https/mailto/tel). Relative URLs resolve against the current origin and
 * are therefore considered safe. Returns false for empty/invalid inputs.
 */
export const isSafeUrl = (url: string | null | undefined): boolean => {
  if (!url) {
    return false;
  }
  try {
    const parsed = new URL(url, window.location.href);
    return SAFE_URL_SCHEMES.includes(parsed.protocol);
  } catch {
    return false;
  }
};

/**
 * Opens `url` in a new context only when it passes `isSafeUrl`. Always applies
 * `noopener,noreferrer`. Returns null (and opens nothing) for unsafe/invalid
 * URLs, so callers can safely pass attacker/third-party-controlled values.
 */
export const openExternalUrl = (
  url: string | null | undefined,
  target = '_blank'
): Window | null => {
  if (!isSafeUrl(url)) {
    return null;
  }
  return window.open(url as string, target, 'noopener,noreferrer');
};
